import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Backdrop, Box, ButtonBase, IconButton, Portal } from '@mui/material'

import { CloseRounded, ExpandMoreRounded } from '@mui/icons-material'

import variables from '../../variables.module.scss'
import { Dropdown } from '../UI/Dropdown/Dropdown'
import { WebsocketOption } from './WebsocketOption'
import { SessionStorageKey } from '../../consts'
import useStreamingWebSocket, { THROTTLE_WS_STATE_CHANGE_MS } from '../../hooks/useStreamingWebSocket'
import websocketOptionsDropdownOpenAtom from '../../recoil/websocketOptionsDropdownOpen'
import { useRecoilState } from 'recoil'
import Tooltip from '@mui/material/Tooltip'
import { appDetailedStreamingEnabled } from '../../types/global'
import useSessionStorageState from 'use-session-storage-state'
import { SettingDisabledCard } from '../modals/TrafficSettingsModal/SettingDisabledCard/SettingDisabledCard'
import { InlineCode } from '../UI/InlineCode/InlineCode'

export enum TrafficWebSocketStreamingOption {
  FlowsOnly = 'flow-only',
  Detailed = 'detailed',
}

interface WebsocketOptionsDropdownProps {
  canSwitchOption: boolean
  handleRestartWebsocket: () => void
}

export const WsOptionsNoClickAwayClassName = 'ws-options-dropdown-no-click-away'

export const WebsocketOptionsDropdown: React.FC<WebsocketOptionsDropdownProps> = ({ canSwitchOption, handleRestartWebsocket }) => {
  const wsOptionsButtonRef = useRef(null)

  const [option, setOption] = useSessionStorageState(SessionStorageKey.TrafficWebSocketStreamingOption, {
    defaultValue: appDetailedStreamingEnabled() ?
      TrafficWebSocketStreamingOption.Detailed :
      TrafficWebSocketStreamingOption.FlowsOnly
  })

  const [dropdownOpen, setDropdownOpen] = useRecoilState(websocketOptionsDropdownOpenAtom)
  const [optionsOnHold, setOptionsOnHold] = useState(false)

  const { readyState } = useStreamingWebSocket()

  const handleSetOption = useCallback((newOption: TrafficWebSocketStreamingOption) => {
    if (option !== newOption) {
      setOption(newOption)
      handleRestartWebsocket()
      setOptionsOnHold(true)
      setTimeout(() => {
        setDropdownOpen(false)
      }, THROTTLE_WS_STATE_CHANGE_MS + 200)
    }
  }, [option, readyState])

  useEffect(() => {
    if (canSwitchOption) {
      setTimeout(() => {
        setOptionsOnHold(false)
      }, THROTTLE_WS_STATE_CHANGE_MS + 200)
    }
  }, [canSwitchOption])

  return (
    <Dropdown
      open={dropdownOpen}
      setOpen={setDropdownOpen}
      anchorElement={
        <Tooltip arrow placement='top-end' title='Detail level of streaming data'>
          <ButtonBase
            ref={wsOptionsButtonRef}
            onClick={() => setDropdownOpen(open => !open)}
            sx={{
              marginTop: '2px',
              paddingLeft: '3px',
              paddingRight: '3px',
              height: '26px',
              borderTopRightRadius: '6px',
              borderBottomRightRadius: '6px',
              background: variables.lighterBlueColor,
            }}
          >
            <Box boxSizing='border-box' display='flex' alignItems='center'>
              <ExpandMoreRounded htmlColor={variables.darkBlueColor} sx={{ fontSize: '20px' }} />
            </Box>
          </ButtonBase>
        </Tooltip>
      }
      anchorRef={wsOptionsButtonRef}
      placement='bottom-start'
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [2, 12]
          }
        }
      ]}
      growFrom='right top'
      noClickAwayClassName='ws-options-dropdown-no-click-away'
    >
      <Box
        boxSizing='border-box'
        padding='16px 18px'
        display='flex'
        alignItems='flex-start'
        flexDirection='column'
        gap='10px'
        borderRadius='6px'
        border={`1px solid ${variables.lighterGrayBlueColor}`}
        boxShadow={variables.lightSpreadShadowBottom}
        bgcolor={variables.mainBackgroundColor}
        sx={{
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-7px',
            right: '7px',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid #fff',
          },
        }}
      >
        <Portal>
          <Backdrop
            open={dropdownOpen}
            onClick={() => setDropdownOpen(false)}
            sx={{
              background: variables.backdropColor,
              zIndex: 903,
            }}
          >
            <></>
          </Backdrop>
        </Portal>
        <Box
          boxSizing='border-box'
          width='100%'
          marginBottom='5px'
          display='flex'
          alignItems='center'
          justifyContent='space-between'
        >
          <h1
            style={{
              margin: 0,
              flex: 1,
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: variables.fontColor
            }}
          >
            Detail Level
          </h1>
          <IconButton
            sx={{ height: '22px', width: '22px', borderRadius: '6px' }}
            onClick={() => setDropdownOpen(false)}
          >
            <CloseRounded htmlColor={variables.lightGrayColor} sx={{
              fontSize: '20px',
            }} />
          </IconButton>
        </Box>
        <WebsocketOption
          title='High-Level Metadata Only'
          description="Saves CPU, memory, and disk."
          option={TrafficWebSocketStreamingOption.FlowsOnly}
          chosen={option === TrafficWebSocketStreamingOption.FlowsOnly}
          setChosen={() => handleSetOption(TrafficWebSocketStreamingOption.FlowsOnly)}
          onHold={optionsOnHold}
        />
        <WebsocketOption
          title='Complete Payload Visibility'
          description='May use more CPU, memory, and disk in busy clusters.'
          option={TrafficWebSocketStreamingOption.Detailed}
          chosen={option === TrafficWebSocketStreamingOption.Detailed}
          setChosen={() => handleSetOption(TrafficWebSocketStreamingOption.Detailed)}
          onHold={optionsOnHold}
        />
        <SettingDisabledCard width='100%'>
          <span
            style={{
              width: '100%',
              boxSizing: 'border-box',
              fontFamily: variables.textFontFamily,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 2,
              color: variables.fontColor
            }}
          >
            Change a default value using:<br/>
            <InlineCode>tap.dashboard.completeStreamingEnabled=true/false</InlineCode>
          </span>
        </SettingDisabledCard>
      </Box>
    </Dropdown>
  )
}
